import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Plus, Pencil, Trash2, FolderOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

const COLORS = [
  { name: 'Ámbar', value: '#D97706' },
  { name: 'Marrón', value: '#92400E' },
  { name: 'Chocolate', value: '#7C2D12' },
  { name: 'Rosa', value: '#DB2777' },
  { name: 'Verde', value: '#059669' },
  { name: 'Azul', value: '#2563EB' },
  { name: 'Morado', value: '#7C3AED' },
  { name: 'Gris', value: '#4B5563' },
];

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#D97706'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/categories`, { withCredentials: true });
      setCategories(data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description,
        color: category.color
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '', color: '#D97706' });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/api/categories/${editingCategory.id}`, formData, { withCredentials: true });
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API_URL}/api/categories`, formData, { withCredentials: true });
        toast.success('Categoría creada');
      }
      setModalOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;
    try {
      await axios.delete(`${API_URL}/api/categories/${categoryId}`, { withCredentials: true });
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Categorías</h1>
          <p className="text-stone-500 mt-1">{categories.length} categorías</p>
        </div>
        <Button
          onClick={() => openModal()}
          data-testid="add-category-btn"
          className="h-12 px-6 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold active:scale-[0.98]"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.length === 0 ? (
          <div className="col-span-full text-center py-12 text-stone-500">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            No hay categorías
          </div>
        ) : (
          categories.map((category) => (
            <div
              key={category.id}
              data-testid={`category-card-${category.id}`}
              className="bg-white rounded-2xl border border-stone-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <FolderOpen className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openModal(category)}
                    data-testid={`edit-category-${category.id}`}
                    className="rounded-lg hover:bg-amber-50 hover:text-amber-600"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(category.id)}
                    data-testid={`delete-category-${category.id}`}
                    className="rounded-lg hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1">{category.name}</h3>
              <p className="text-sm text-stone-500">{category.description || 'Sin descripción'}</p>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="category-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="category-name-input"
                className="h-12 rounded-xl"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="category-description-input"
                className="h-12 rounded-xl"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-10 h-10 rounded-xl transition-all ${
                      formData.color === color.value ? 'ring-2 ring-offset-2 ring-stone-900 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="rounded-xl">
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-category-btn" className="rounded-xl bg-amber-600 hover:bg-amber-700">
                {editingCategory ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
